import { useSeo } from '@/lib/seo';
import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { AlertsMap } from '@/components/AlertsMap';
import { AlertOctagon, CloudRain, Truck, Layers, Waves } from 'lucide-react';

const LAYERS = [
  { key: 'road', label: 'Road events', icon: <AlertOctagon className="w-4 h-4" />, color: '#E11D48' },
  { key: 'weather', label: 'Weather', icon: <CloudRain className="w-4 h-4" />, color: '#0EA5E9' },
  { key: 'freight', label: 'Freight-relevant', icon: <Truck className="w-4 h-4" />, color: '#F7941D' },
];

export default function LiveMap() {
  useSeo({ title: 'Live NZ Road, Weather & Freight Map', description: 'A full-screen live map of New Zealand road events, MetService weather warnings and freight restrictions.' });
  const [alerts, setAlerts] = useState([]);
  const [quakes, setQuakes] = useState([]);
  const [on, setOn] = useState({ road: true, weather: true, freight: true, quakes: false });

  useEffect(() => {
    api.get('/alerts?status_filter=Active').then((r) => setAlerts(r.data)).catch(() => {});
    api.get('/quakes?limit=50').then((r) => setQuakes(r.data || [])).catch(() => {});
  }, []);

  const visible = alerts.filter((a) => {
    const isWeather = a.source === 'metservice';
    const isFreight = !!a.freight_relevant || a.source === 'restriction_change';
    if (isWeather) return on.weather;
    if (isFreight) return on.freight || on.road;
    return on.road;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="border-b border-zinc-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-700"><Layers className="w-4 h-4 text-teal-600" /> Live NZ map</span>
          <div className="flex gap-2 flex-wrap" data-testid="map-layer-toggles">
            {LAYERS.map((l) => (
              <button key={l.key} onClick={() => setOn((s) => ({ ...s, [l.key]: !s[l.key] }))}
                data-testid={`map-layer-${l.key}`}
                className={`chip inline-flex items-center gap-1.5 text-sm transition-colors ${on[l.key] ? 'text-white' : 'text-zinc-500 bg-zinc-100'}`}
                style={on[l.key] ? { background: l.color } : {}}>
                {l.icon}{l.label}
              </button>
            ))}
            {quakes.length > 0 && (
              <button onClick={() => setOn((s) => ({ ...s, quakes: !s.quakes }))}
                data-testid="map-layer-quakes"
                className={`chip inline-flex items-center gap-1.5 text-sm transition-colors ${on.quakes ? 'text-white' : 'text-zinc-500 bg-zinc-100'}`}
                style={on.quakes ? { background: '#7c3aed' } : {}}>
                <Waves className="w-4 h-4" />Earthquakes
              </button>
            )}
          </div>
          <span className="ml-auto text-xs text-zinc-500" data-testid="map-count">{visible.filter((a) => a.lat && a.lng).length} located · {visible.length} total{on.quakes && quakes.length ? ` · ${quakes.length} quakes` : ''}</span>
        </div>
      </div>
      <div className="flex-1" style={{ minHeight: 'calc(100vh - 128px)' }} data-testid="live-map-wrap">
        <AlertsMap alerts={visible} quakes={on.quakes ? quakes : []} height="calc(100vh - 128px)" />
      </div>
    </div>
  );
}
